import './form-select.scss'
import { uniqueId, isEmpty, debounce } from 'lodash'
import { defineComponent, VNode, PropType, h } from 'vue'
import NrFormLabel from '../form-elements/nr-form-label.vue'

interface Option {
  name: string
  value: string | number
}

interface ValidationProp {
  [key: string]: { message: string; validate(value: string): Promise<boolean> }
}

const COMPONENT_NAME = 'nr_form_select'

export default defineComponent({
  name: COMPONENT_NAME,
  components: { NrFormLabel },

  mounted() {
    this.validate = debounce(this.validate, 300)
  },

  props: {
    label: String,
    name: String,
    help: String,
    hideLabel: { type: Boolean, default: false },
    includeBlank: Boolean,
    options: Array as PropType<Option[]>,
    required: Boolean,
    modelValue: String,
    validations: {
      type: Object as PropType<ValidationProp>,
      default() {
        return {}
      }
    }
  },

  emits: ['update:modelValue', 'on-validate', 'keydown', 'blur', 'click'],

  data: () => {
    return {
      id: uniqueId(`${COMPONENT_NAME}-`),
      errors: {}
    }
  },

  computed: {
    computedValidations(): {
      [key: string]: {
        message: string
        validate: (value: string) => Promise<Boolean>
      }
    } {
      if (this.required) {
        return Object.assign({}, this.validations, {
          required: {
            message: 'This field is required.',
            validate(value?: string) {
              return !!(value || '').trim()
            }
          }
        })
      }

      return this.validations
    }
  },

  methods: {
    validate() {
      Object.keys(this.computedValidations).forEach(async (key) => {
        try {
          if ((this.modelValue || '').trim() || this.required) {
            const errorKey = key as keyof typeof this.errors
            const isValid = await this.computedValidations[key].validate(
              this.modelValue as string
            )
            if (isValid) {
              delete this.errors[errorKey]
              this.errors = Object.assign({}, this.errors)
            } else {
              throw new Error(`${key} is not valid.`)
            }
          }

          this.$emit('on-validate', true)
        } catch (_error) {
          this.$emit('on-validate', false)
          this.errors = Object.assign({}, this.errors, {
            [`${key}`]: this.computedValidations[key].message
          })
        }
      })
    },

    generateAdditionalContent(): VNode[] {
      const nodes: VNode[] = []

      if (this.help) {
        nodes.push(h('p', { class: 'help' }, this.help))
      }

      const errors = Object.keys(this.errors)

      if (errors.length) {
        errors.forEach((error) => {
          const errorKey = error as keyof typeof this.errors
          nodes.push(h('p', { class: 'help is-danger' }, this.errors[errorKey]))
        })
      }

      return nodes
    },

    generateLabel(): string {
      if (this.$slots.label) {
        return this.$slots.label.toString()
      } else {
        return this.required ? `* ${this.label}` : `${this.label}`
      }
    }
  },

  render(this: any) {
    const self = this
    return h('div', { class: 'field' }, [
      h(
        NrFormLabel,
        {
          class: `nr-label ${self.hideLabel ? 'is-sr-only' : ''}`,
          for: self.id
        },
        self.generateLabel
      ),

      h('div', { class: 'control' }, [
        h(
          'div',
          { class: ['select', { 'is-danger': !isEmpty(self.errors) }] },
          [
            h(
              'select',
              {
                ...self.$attrs,
                value: self.modelValue,
                name: self.name,
                required: self.required,
                disabled: self.$attrs.disabled,
                id: self.id,
                onInput: (event: InputEvent) => {
                  self.validate()
                  self.$emit(
                    'update:modelValue',
                    (event.target as HTMLSelectElement).value
                  )
                },
                onKeyDown: (event: KeyboardEvent) => {
                  self.validate()
                  self.$emit('keydown', event)
                },
                onBlur: (event: MouseEvent) => {
                  self.validate()
                  self.$emit('blur', event)
                },
                onClick: (event: MouseEvent) => {
                  self.validate()
                  self.$emit('click', event)
                }
              },
              [
                self.includeBlank
                  ? h(
                      'option',
                      {
                        value: '',
                        selected: !!self.modelValue
                      },
                      '-'
                    )
                  : null,
                ...self.options.map((option: Option): VNode => {
                  return h(
                    'option',
                    {
                      value: option.value,
                      selected: self.modelValue === option.value,
                      key: `${self.id}-option-${option.value}`
                    },
                    option.name
                  )
                })
              ].filter((node) => Boolean(node))
            )
          ]
        ),
        ...self.generateAdditionalContent()
      ])
    ])
  }
})
